import CFACellMeasurerCache from "./CFACellMeasurerCache";

// We're extend the cell measurer cache so that we can make the columns expand
// to fill the whole page, like an html table.
export class CellFillWidthCache extends CFACellMeasurerCache {
    private isDirty = true;
    private fillWidthsCache: number[] = [];
    private containerWidth: number = 100;

    public set(
        rowIndex: number,
        columnIndex: number,
        width: number,
        height: number,
    ): void {
        this.isDirty = true;
        return super.set(rowIndex, columnIndex, width, height);
    }

    public setContainerWidth(width: number) {
        if (width !== this.containerWidth) {
            this.isDirty = true;
        }
        this.containerWidth = width;
    }

    // Override
    public clearAll(): void {
        this.isDirty = true;
        this.fillWidthsCache = [];
        return super.clearAll();
    }

    // Override
    public clear(rowIndex: number, columnIndex: number = 0) {
        this.isDirty = true;
        this.fillWidthsCache = [];
        return super.clear(rowIndex, columnIndex);
    }

    // Override
    public columnWidth = ({ index }: { index: number }) => {
        if (this.isDirty) {
            this.calculateColumnWidths();
        }

        if (index >= 0 && index < this.fillWidthsCache.length) {
            return this.fillWidthsCache[index];
        }

        // If we're asking for a column that we don't have data for, we'll fall back
        // to returning the default width.
        return this._defaultWidth;
    };

    private calculateColumnWidths() {
        this.isDirty = false;

        if (this._columnCount === 0) {
            return;
        }

        // If we have more rows than fit on the page, some of our width is taken up by a scrollbar.
        // Actual known max is 17, we're adding a few pixels to be more cautious.
        const scrollbarMaxWidth = 20; 

        // Add up the total of all columns + possible scrollbar. If this is less than the container width,
        // then we'll add to the widths to make them fill the container.
        let total = scrollbarMaxWidth;
        const unpaddedWidths: number[] = new Array(this._columnCount);

        for (let i = 0; i < this._columnCount; i++) {
            // Use the calculated max width for the column.
            let width = this._columnWidthCache[i];

            // If it's not available yet, use the default.
            if (width === undefined) {
                width = this._defaultWidth;
            }

            unpaddedWidths[i] = width;
            total += width;
        }

        // If the columns don't fill the container, we'll pad them evenly.
        let pad = 0;
        if (total < this.containerWidth) {
            pad = (this.containerWidth - total) / this._columnCount;
        }

        // Cache these widths
        for (let i = 0; i < this._columnCount; i++) {
            this.fillWidthsCache[i] = unpaddedWidths[i] + pad;
        }
    }
}
